import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { ToWords } from "to-words";
const logo = require("../Images/FullLogo_NoBuffer__4_-removebg-preview.png");


const cleanText = (text, maxLength = 60) => {
  if (!text) return "";
  return text
    .replace(/[^\w\s\-.,:]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .substring(0, maxLength);
};


const formatItinerary = (itineraryArray) => {
  if (!itineraryArray || itineraryArray.length === 0) {
    return [
      {
        dayNum: 1,
        title: "Standard Tour",
        details: "Complete itinerary details available via WhatsApp",
      },
    ];
  }

  return itineraryArray.map((day) => ({
    dayNum: day.day || 1,
    title: cleanText(day.title || "Day Activity"),
    details: cleanText(day.details || "Details coming soon", 150),
  }));
};

// Change param name for clarity
const formatInclusions = (inclusionsArray) => {  // ← inclusionsArray
  if (!inclusionsArray || !Array.isArray(inclusionsArray) || inclusionsArray.length === 0) {
    return [];
  }
  return inclusionsArray.map((item) => cleanText(item, 50));
};

const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    paddingTop: 25,
    paddingLeft: 25,
    paddingRight: 25,
    // Reserves room so flowing content never runs under the fixed footer
    paddingBottom: 95,
    fontFamily: "Helvetica",
    lineHeight: 1.4,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#f0a027",
    paddingBottom: 10,
  },
  companySection: {
    flex: 2,
  },
  invoiceInfo: {
    flex: 1,
    alignItems: "flex-end",
  },
  logo: { width: 120 },
  companyName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#333",
    marginTop: 5,
    marginBottom: 5,
  },
  companyDetails: {
    fontSize: 9,
    color: "#666",
    marginTop: 4,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#f0a027",
    marginVertical: 10,
  },
  table: {
    width: "100%",
    marginVertical: 5,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    alignItems: "center",
    minHeight: 24,
  },
  tableColHeader: {
    width: "25%",
    fontWeight: "bold",
    backgroundColor: "#f8f8f8",
    paddingVertical: 8,
    paddingHorizontal: 6,
    color: "#333",
  },
  tableCol: {
    width: "25%",
    paddingVertical: 5,
    paddingHorizontal: 4,
    color: "#444",
  },
  itinerarySection: {
    backgroundColor: "#f0f9ff",
    borderRadius: 6,
    padding: 12,
    borderWidth: 1,
    borderColor: "#0ea5e9",
    marginVertical: 8,
  },
  itineraryDay: {
    marginBottom: 8,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#e0f2fe",
  },
  dayHeader: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#0369a1",
    marginBottom: 3,
  },
  dayDetails: {
    fontSize: 9,
    lineHeight: 1.4,
    color: "#1e3a8a",
  },
  inclusionsSection: {
    backgroundColor: "#f0fdf4",
    borderRadius: 6,
    padding: 10,
    borderWidth: 1,
    borderColor: "#22c55e",
    marginVertical: 6,
  },
  inclusionsTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#166534",
    marginBottom: 6,
  },
  inclusionItem: {
    fontSize: 9,
    marginBottom: 2,
    paddingLeft: 8,
  },
  amountSection: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 15,
  },
  totalAmount: {
    width: "35%",
    borderWidth: 2,
    borderColor: "#f0a027",
    padding: 10,
    backgroundColor: "#fff9f0",
    borderRadius: 4,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 25,
    right: 25,
    fontSize: 8,
    color: "#666",
    textAlign: "center",
    borderTopWidth: 1,
    borderTopColor: "#eee",
    paddingTop: 8,
  },
  longCol: {
    width: "75%", // Description column
    paddingVertical: 8,
    paddingHorizontal: 6,
    color: "#444",
  },
});

const toWords = new ToWords();
const convertToWords = (amount) => {
  if (!amount || isNaN(amount)) return "Zero Rupees";
  return toWords.convert(amount, { currency: true, ignoreDecimal: true });
};

// Read directly from the persisted fields — no more guessing/fallback via _id
const getBookingRef = (bookingData) => {
  return bookingData?.bookingReference || "TGH-TBK-0001";
};

const getInvoiceNumber = (bookingData) => {
  return bookingData?.invoiceNumber || "TGH-INV-0001";
};

const TourInvoicePDF = ({ invoiceData }) => {
  if (!invoiceData) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text>No invoice data available.</Text>
        </Page>
      </Document>
    );
  }

  const tour = invoiceData.tourId || {};
  const bookingRef = getBookingRef(invoiceData);
    const invoiceNumber = getInvoiceNumber(invoiceData);
  const travelDate = invoiceData.date
    ? new Date(invoiceData.date).toLocaleDateString("en-IN", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "N/A";
  const guests = invoiceData.guests || 1;
  const totalPaid = Number(invoiceData?.amountPaid || invoiceData?.amount || 0);
  const pricing = invoiceData?.pricing; // From { ...booking, pricing }

  const gstRate = Number(pricing?.data?.gst_price ?? 18);
  let baseAmount = totalPaid;
  let gstAmount = 0;

  if (gstRate > 0 && totalPaid > 0) {
    baseAmount = Math.round(totalPaid / (1 + gstRate / 100));
    gstAmount = totalPaid - baseAmount;
  }

 
  const formattedItinerary = formatItinerary(tour.itinerary);

  
  const formattedInclusions = formatInclusions(tour.inclusions || []);

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* Header */}
        <View style={styles.header}>
          <View style={styles.companySection}>
            <Image style={styles.logo} src={logo} />
            <Text style={styles.companyName}>The Great Himalayas Tours</Text>
            <Text style={styles.companyDetails}>
              Siliguri, Darjeeling(Dist), West Bengal, 734001{"\n"}
              GSTIN: 09AAHCH9024K1Z7{"\n"}
              Phone: +91 98835 87965 | Email: contact@thegreathimalayas.com
            </Text>
          </View>

      <View style={styles.invoiceInfo}>
            <Text style={{ ...styles.sectionTitle, marginTop: 0 }}>
              TOUR INVOICE & ITINERARY
            </Text>
            <Text style={styles.companyDetails}>
              Invoice No: {invoiceNumber}
              {"\n"}
              Booking Ref: {bookingRef}
              {"\n"}
              Date: {new Date().toLocaleDateString()}
            </Text>
          </View>
        </View>

        {/* Guest Information */}
        <Text style={styles.sectionTitle}>Guest Information</Text>
        <View style={styles.table} wrap={false}>
          <View style={styles.tableRow}>
            <Text style={styles.tableColHeader}>Guest Name</Text>
            <Text style={styles.tableColHeader}>Contact</Text>
            <Text style={styles.tableColHeader}>Travel Date</Text>
            <Text style={styles.tableColHeader}>Guests</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCol}>
              {invoiceData.firstName} {invoiceData.lastName}
            </Text>
            <Text style={styles.tableCol}>
              {invoiceData.email}
              {"\n"}
              {invoiceData.phone}
            </Text>
            <Text style={styles.tableCol}>{travelDate}</Text>
            <Text style={styles.tableCol}>{guests}</Text>
          </View>
        </View>

        {/* Tour Details */}
        <Text style={styles.sectionTitle}>Tour Details</Text>
        <View style={styles.table} wrap={false}>
          <View style={styles.tableRow}>
            <Text style={styles.tableColHeader}>Tour Name</Text>
            <Text style={styles.tableColHeader}>Location</Text>
            <Text style={styles.tableColHeader}>Duration</Text>
            <Text style={styles.tableColHeader}>Special Requests</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCol}>
              {cleanText(tour.title) || "N/A"}
            </Text>
            <Text style={styles.tableCol}>
              {cleanText(tour.location) || "N/A"}
            </Text>
            <Text style={styles.tableCol}>{tour.duration || "-"}</Text>
            <Text style={styles.tableCol}>
              {cleanText(invoiceData.specialRequests) || "-"}
            </Text>
          </View>
        </View>

        {/* ✅ COMPLETE ITINERARY SECTION - all days, kept together as one block */}
        <Text style={styles.sectionTitle}>Detailed Itinerary</Text>
        <View style={styles.itinerarySection} wrap={false}>
          {formattedItinerary.map((day, index) => (
            <View key={index} style={styles.itineraryDay}>
              <Text style={styles.dayHeader}>
                Day {day.dayNum}: {day.title}
              </Text>
              <Text style={styles.dayDetails}>{day.details}</Text>
            </View>
          ))}
        </View>

        {/* ✅ INCLUSIONS SECTION - all items, kept together as one block */}
        <Text style={styles.sectionTitle}>What's Included</Text>
        <View style={styles.inclusionsSection} wrap={false}>
          <Text style={styles.inclusionsTitle}>✅ Package Inclusions</Text>
          {formattedInclusions.map((inclusion, index) => (
            <Text key={index} style={styles.inclusionItem}>
              • {inclusion}
            </Text>
          ))}
        </View>

        {/* Payment Details */}
        <Text style={styles.sectionTitle}>Payment Details</Text>

        <View style={styles.table} wrap={false}>
          <View style={[styles.tableRow, { backgroundColor: '#f8f8f8' }]}>
            <Text style={[styles.longCol, { fontWeight: 'bold' }]}>Description</Text>
            <Text style={styles.tableColHeader}>Amount (₹)</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.longCol}>
              Complete Tour Package ({guests} guest{guests > 1 ? 's' : ''})
            </Text>
            <Text style={styles.tableCol}>
              ₹ {baseAmount.toLocaleString('en-IN')}
            </Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.longCol}>GST ({gstRate}%)</Text>
            <Text style={styles.tableCol}>
              ₹ {gstAmount.toLocaleString('en-IN')}
            </Text>
          </View>

          <View style={[styles.tableRow, { borderTopWidth: 2, borderTopColor: '#f0a027' }]}>
            <Text style={[styles.longCol, { fontWeight: 'bold' }]}>GRAND TOTAL</Text>
            <Text style={[styles.tableCol, { fontWeight: 'bold' }]}>
              ₹ {totalPaid.toLocaleString('en-IN')}
            </Text>
          </View>
        </View>

        {/* Total box - kept together as one unit */}
        <View style={styles.amountSection} wrap={false}>
          <View style={styles.totalAmount}>
            <Text style={{ fontWeight: "bold", fontSize: 12 }}>
              GRAND TOTAL
            </Text>
            <Text style={{ fontSize: 16, marginTop: 4 }}>
              ₹ {totalPaid.toLocaleString('en-IN')}
            </Text>
            <Text style={{ fontSize: 9, marginTop: 4 }}>
              {convertToWords(totalPaid)} Only
            </Text>
          </View>
        </View>

        {/* Footer - `fixed` renders it correctly on every page; page's
            paddingBottom above reserves the space so content never overlaps it */}
        <View style={styles.footer} fixed>
          <Text>
            Thank you for choosing The Great Himalayas Hotels! {'\n'}

            • Cancellation Policy:{'\n'}
            100% refund if cancelled 72 hours before check-in •
            50% refund if cancelled between 24 to 72 hours •
            No refund within 24 hours of check-in {'\n'}

            • For queries: +91 98835 87965 | contact@thegreathimalayas.com {'\n'}
            • This is a computer generated invoice • Terms & Conditions apply
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default TourInvoicePDF;
